import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

export type Theme = "light" | "dark";

export interface ThemeContextType {
  theme: Theme;
  setTheme: (value: Theme) => void;
  toggleTheme: () => void;
  mounted: boolean;
  resolvedTheme: Theme;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_KEY = "forfada-theme";

function getTimeBasedTheme(): Theme {
  if (typeof window === "undefined") return "light";
  const hour = new Date().getHours();
  // Das 06:00 às 17:59 é dia (claro), das 18:00 às 05:59 é noite (escuro)
  return hour >= 6 && hour < 18 ? "light" : "dark";
}

function getStoredTheme(): Theme | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = localStorage.getItem(THEME_KEY);
    return stored === "dark" || stored === "light" ? stored : null;
  } catch {
    return null;
  }
}

function setStoredTheme(theme: Theme): void {
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    // localStorage unavailable
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => getStoredTheme() || getTimeBasedTheme());
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const root = document.documentElement;

    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }, [theme]);

  useEffect(() => {
    // Verifica o horário a cada hora para alternar automaticamente se não houver escolha manual
    const interval = setInterval(() => {
      if (!localStorage.getItem(THEME_KEY)) {
        const nextTheme = getTimeBasedTheme();
        setTheme(nextTheme);
      }
    }, 1000 * 60 * 60);

    return () => clearInterval(interval);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      setStoredTheme(next);
      return next;
    });
  }, []);

  const setThemeValue = useCallback((value: Theme) => {
    setStoredTheme(value);
    setTheme(value);
  }, []);

  const value = useMemo<ThemeContextType>(
    () => ({
      theme,
      setTheme: setThemeValue,
      toggleTheme,
      mounted,
      resolvedTheme: mounted ? theme : "light",
    }),
    [theme, mounted, setThemeValue, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    return {
      theme: "light",
      setTheme: () => {},
      toggleTheme: () => {},
      mounted: false,
      resolvedTheme: "light",
    };
  }
  return context;
}